function _defineProperty(obj, key, value) { if (key in obj) { Object.defineProperty(obj, key, { value: value, enumerable: true, configurable: true, writable: true }); } else { obj[key] = value; } return obj; }

import React from 'react';
import PropTypes from 'prop-types';
import Icon from '../../Icon';
import { classNamesFor } from '../../utils/classNames';
var cx;
/**
 * The PlayerName component handles presentation of a players name with given attributes
 * for consistent display throughout an SIE application. It handles properly truncating the
 * name while preserving the icons to the right. This is surprisingly difficult to do :(.
 *
 * ## Composed of
 * - [Icon](../Icon/README.md)
 *
 * ## Related Components
 * - [Player](./Player/README.md)
 *
 * ## Usage
 *
 * ```jsx
 * <PlayerName
 *    name="Bob Toss"
 *    onlineId="gammagamer"
 *    isOfficiallyVerified
 *    isPsPlusMember
 * />
 * ```
 *
 * ## Props
 */

var PlayerName = function PlayerName(props) {
  var qa = props.qa,
      className = props.className,
      name = props.name,
      onlineId = props.onlineId,
      isOfficiallyVerified = props.isOfficiallyVerified,
      isPsPlusMember = props.isPsPlusMember,
      psPlusLabel = props.psPlusLabel,
      verifiedLabel = props.verifiedLabel;
  var isOnlineId = !name;

  var _PlayerName$getClassN = PlayerName.getClassName({
    className: className,
    isOnlineId: isOnlineId
  }),
      root = _PlayerName$getClassN.root,
      nameClass = _PlayerName$getClassN.name;

  return /*#__PURE__*/React.createElement("span", {
    "data-qa": qa,
    className: root
  }, /*#__PURE__*/React.createElement("span", {
    className: nameClass
  }, name || onlineId), isOfficiallyVerified && /*#__PURE__*/React.createElement(Icon, {
    qa: "".concat(qa, "#verifiedIcon"),
    type: "verified-user-badge",
    size: "s",
    label: verifiedLabel
  }), isPsPlusMember && /*#__PURE__*/React.createElement(Icon, {
    qa: "".concat(qa, "#psPlusIcon"),
    type: "ps-plus",
    size: "s",
    label: psPlusLabel
  }));
};

PlayerName.getClassName = function (_ref) {
  var className = _ref.className,
      isOnlineId = _ref.isOnlineId;
  return {
    root: cx('psw-player-name', 'psw-l-line-left', className),
    name: cx('psw-player-name-text', 'psw-truncate-text-1', _defineProperty({}, cx('psw-text-italic', 'psw-p-r-2xs'), isOnlineId))
  };
};

PlayerName.useClassNames = function (classNames) {
  PlayerName.classNames = classNames;
};

PlayerName.defaultProps = {
  qa: '',
  className: '',
  name: '',
  onlineId: '',
  isOfficiallyVerified: false,
  isPsPlusMember: false,
  verifiedLabel: '',
  psPlusLabel: ''
};
PlayerName.propTypes = {
  /**
   * QA selector attribute used to target important elements for test validation
   */
  qa: PropTypes.string,

  /**
   * Allows adding a custom class or series of classes on the component
   * useful for setting margin or padding
   */
  className: PropTypes.string,

  /**
   * The users real name
   */
  name: PropTypes.oneOfType([PropTypes.string, PropTypes.node]),

  /**
   * The users online Id
   */
  onlineId: PropTypes.oneOfType([PropTypes.string, PropTypes.node]),

  /**
   * Whether the user is officially verified
   */
  isOfficiallyVerified: PropTypes.bool,

  /**
   * Whether the user is a PS Plus subscriber
   */
  isPsPlusMember: PropTypes.bool,

  /**
   * The label for screen readers to announce that the user is a ps plus member
   */
  psPlusLabel: PropTypes.string,

  /**
   * The label for screen readers to announce that the user is officially verified
   */
  verifiedLabel: PropTypes.string
};
cx = classNamesFor(PlayerName);
export default PlayerName;